import React from 'react'
import { ThumbsUp, ThumbsDown } from 'lucide-react'

interface EmptyStateProps {
  projectId: string
  projectName: string
}

export const EmptyState = ({ projectId, projectName }: EmptyStateProps) => {
  return (
    <>
      <style>
        {`
          @keyframes runAway {
            0% {
              transform: translateX(-40px) rotate(0deg);
            }
            25% {
              transform: translateX(25vw) translateY(-10px) rotate(-15deg);
            }
            50% {
              transform: translateX(50vw) translateY(10px) rotate(15deg);
            }
            75% {
              transform: translateX(75vw) translateY(-5px) rotate(-10deg);
            }
            100% {
              transform: translateX(calc(100vw + 40px)) rotate(0deg);
            }
          }

          @keyframes chaseAfter {
            0% {
              transform: translateX(-100px) rotate(0deg);
            }
            25% {
              transform: translateX(calc(25vw - 60px)) translateY(-10px) rotate(15deg);
            }
            50% {
              transform: translateX(calc(50vw - 60px)) translateY(10px) rotate(-15deg);
            }
            75% {
              transform: translateX(calc(75vw - 60px)) translateY(-5px) rotate(10deg);
            }
            100% {
              transform: translateX(calc(100vw - 20px)) rotate(0deg);
            }
          }

          @keyframes runAwayReverse {
            0% {
              transform: translateX(calc(100vw + 40px)) rotate(0deg);
            }
            25% {
              transform: translateX(75vw) translateY(10px) rotate(15deg);
            }
            50% {
              transform: translateX(50vw) translateY(-10px) rotate(-15deg);
            }
            75% {
              transform: translateX(25vw) translateY(5px) rotate(10deg);
            }
            100% {
              transform: translateX(-40px) rotate(0deg);
            }
          }

          @keyframes chaseAfterReverse {
            0% {
              transform: translateX(calc(100vw + 100px)) rotate(0deg);
            }
            25% {
              transform: translateX(calc(75vw + 60px)) translateY(10px) rotate(-15deg);
            }
            50% {
              transform: translateX(calc(50vw + 60px)) translateY(-10px) rotate(15deg);
            }
            75% {
              transform: translateX(calc(25vw + 60px)) translateY(5px) rotate(-10deg);
            }
            100% {
              transform: translateX(20px) rotate(0deg);
            }
          }

          .animate-runAway {
            animation: runAway 8s ease-in-out infinite;
          }
          
          .animate-chaseAfter {
            animation: chaseAfter 8s ease-in-out infinite;
          }
          
          .animate-runAway-delay-2 {
            animation: runAway 10s ease-in-out infinite;
            animation-delay: 2s;
          }
          
          .animate-chaseAfter-delay-2 {
            animation: chaseAfter 10s ease-in-out infinite;
            animation-delay: 2s;
          }
          
          .animate-runAway-delay-4 {
            animation: runAway 12s ease-in-out infinite;
            animation-delay: 4s;
          }
          
          .animate-chaseAfter-delay-4 {
            animation: chaseAfter 12s ease-in-out infinite;
            animation-delay: 4s;
          }
          
          .animate-runAwayReverse {
            animation: runAwayReverse 9s ease-in-out infinite;
            animation-delay: 1s;
          }
          
          .animate-chaseAfterReverse {
            animation: chaseAfterReverse 9s ease-in-out infinite;
            animation-delay: 1s;
          }
        `}
      </style>
      <div className="flex flex-col items-center justify-center py-16 px-4 border rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-8 h-[80vh] relative overflow-hidden">
        {/* Animated thumbs chasing each other */}
        <div className="absolute inset-0 pointer-events-none">
          {/* First chase group */}
          <div className="absolute top-[20%] left-0 w-full h-12 opacity-20">
            <div className="relative w-full h-full">
              {/* Thumbs down running away */}
              <ThumbsDown className="absolute w-8 h-8 text-[#FF4A8E] animate-runAway" />
              {/* Thumbs up chasing */}
              <ThumbsUp className="absolute w-8 h-8 text-[#FF4A8E] animate-chaseAfter" />
            </div>
          </div>

          {/* Second chase group with delay */}
          <div className="absolute top-[40%] left-0 w-full h-12 opacity-15">
            <div className="relative w-full h-full">
              {/* Thumbs down running away */}
              <ThumbsDown className="absolute w-10 h-10 text-[#FF4A8E] animate-runAway-delay-2" />
              {/* Thumbs up chasing */}
              <ThumbsUp className="absolute w-10 h-10 text-[#FF4A8E] animate-chaseAfter-delay-2" />
            </div>
          </div>

          {/* Third chase group */}
          <div className="absolute top-[60%] left-0 w-full h-12 opacity-10">
            <div className="relative w-full h-full">
              {/* Thumbs down running away */}
              <ThumbsDown className="absolute w-6 h-6 text-[#FF4A8E] animate-runAway-delay-4" />
              {/* Thumbs up chasing */}
              <ThumbsUp className="absolute w-6 h-6 text-[#FF4A8E] animate-chaseAfter-delay-4" />
            </div>
          </div>

          {/* Fourth chase group going right to left */}
          <div className="absolute top-[80%] left-0 w-full h-12 opacity-20">
            <div className="relative w-full h-full">
              {/* Thumbs down running away */}
              <ThumbsDown className="absolute w-7 h-7 text-[#FF4A8E] animate-runAwayReverse" />
              {/* Thumbs up chasing */}
              <ThumbsUp className="absolute w-7 h-7 text-[#FF4A8E] animate-chaseAfterReverse" />
            </div>
          </div>
        </div>

        {/* Feedback icon */}
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 relative z-10">
          <ThumbsUp className="w-8 h-8 text-[#FF4A8E]" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 relative z-10">
          No feedback collected yet
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-6 relative z-10">
          Feedback will be automatically collected from your meetings and conversations. They help track customer
          sentiments and insights.
        </p>
      </div>
    </>
  )
}
